import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux';
import { 
    LICENCE_STATUS_ACTIVE, 
    LICENCE_STATUS_EXPIRED, 
    LICENCE_STATUS_REVOKED, 
    LICENCE_STATUS_SUSPENDED, 
    LICENCE_STATUS_WITHDRAWN, 
    LICENCE_STATUS_WITHDRAWN_BY_CLIENT, 
    REQUEST_STATUS
} from '../../../../app/constants';
import { fullName } from '../../../../app/utils';

import { 
    Button, 
    ErrorMark, 
    LineSeperator 
} from "../../../../components/RegularComponents";
import { 
    setCurrentAgentLicenceStatus, 
    setCurrentAgentLicenceExpiryDate 
} from '../../../../redux/slice/additionalApplicationsSlice';
import { sendEmail } from '../../../../redux/slice/bltDecisionSlice';
import { fetchApplicationWithId, setAllAgentLicenceStatus } from '../../../../redux/slice/bltHomeSlice';
import { saveBusinessLicenceApplicationData } from '../../../../redux/slice/businessLicenceApplicationSlice';
import { setAgencyApplicationLicenceExpiryDate, setAgencyApplicationLicenceStatus } from '../../../../redux/slice/primaryAgencyApplicationSlice';

export const EditLicenceStatusTab = (props) => {
    
    const { 
        licenceApplication,
        isAgencyApplication,
        applicationType,
        applicationMode,
        hideEditLicenceStatusTab 
    } = props
    const dispatch = useDispatch()
    const [submitRequestStatus, setSubmitRequestStatus] = useState(REQUEST_STATUS.IDLE)
    const [fetchAppRequestStatus, setFetchRequestStatus] = useState(REQUEST_STATUS.IDLE)
    const [requestError, setRequestError] = useState(null)
    const [licenceStatus, setLicenceStatus] = useState(licenceApplication.licenceStatus ?? LICENCE_STATUS_ACTIVE)
    const [emailMessage, setEmailMessage] = useState(null)
    const [primaryAgencyApplication, setPrimaryAgencyApplication] = useState(isAgencyApplication ? licenceApplication : null)

    useEffect(() => {
        if(!primaryAgencyApplication 
            && fetchAppRequestStatus !== REQUEST_STATUS.IN_PROGRESS) { 
            setFetchRequestStatus(REQUEST_STATUS.IN_PROGRESS)
            dispatch(fetchApplicationWithId(licenceApplication.businessApplicationId))
            .then(app => {
                setPrimaryAgencyApplication(app)
            })
            .catch(err => {
                setPrimaryAgencyApplication({})
            })
            .finally(() => {
                setFetchRequestStatus(REQUEST_STATUS.IDLE)
            })
        }
    }, [
        primaryAgencyApplication,
        fetchAppRequestStatus,
        setFetchRequestStatus,
        licenceApplication,
        dispatch
    ])
    
    const handleLicenceStatusChange = (e) => {
        setLicenceStatus(e.target.value)
    }

    const businessContactName = useMemo(() => {
        if(isAgencyApplication) { 
            return fullName(licenceApplication?.businessLicenceData?.mainBusinessContact)
        } else if(primaryAgencyApplication){ 
            return fullName(primaryAgencyApplication.businessLicenceData?.mainBusinessContact)
        }

        return ""
    }, [isAgencyApplication, licenceApplication, primaryAgencyApplication])

    const sendMessageToAgency = (emailId) => {
        if(!emailMessage)  {
            // just return silently.
            hideEditLicenceStatusTab()
            return
        }

        if(!emailId) {
            setRequestError("could not send message to agency : email of business contact is not available")
        }
    
        const payloadData = {
            toEmail : [emailId],
            subject : "Change in licence status",
            body : emailMessage
        }

        setSubmitRequestStatus(REQUEST_STATUS.IN_PROGRESS)

        dispatch(sendEmail(payloadData))
            .then(data => {
                // email sent successfully
                hideEditLicenceStatusTab()
            })
            .catch(error => {
                // error in sending email
                setRequestError("error in sending message to agency")
            })
            .finally(() => {
                setSubmitRequestStatus(REQUEST_STATUS.IDLE)
            })
    }

    const handleSubmit = () => {

        setRequestError(null)

        if(isAgencyApplication) {
            
            dispatch(setAgencyApplicationLicenceStatus(licenceStatus))

            if(licenceStatus === LICENCE_STATUS_EXPIRED
                || licenceStatus === LICENCE_STATUS_REVOKED
                || licenceStatus === LICENCE_STATUS_SUSPENDED
                || licenceStatus === LICENCE_STATUS_WITHDRAWN
                || licenceStatus === LICENCE_STATUS_WITHDRAWN_BY_CLIENT) {

                const expiryDate = new Date().getTime()

                dispatch(setAgencyApplicationLicenceExpiryDate(expiryDate))

                // set collector and debt management agent's status same as application's status.
                dispatch(setAllAgentLicenceStatus({
                    licenceStatus : licenceStatus,
                    endDate : expiryDate
                }))
            }
        } else {
            
            dispatch(setCurrentAgentLicenceStatus(licenceStatus))

            if(licenceStatus === LICENCE_STATUS_EXPIRED
                || licenceStatus === LICENCE_STATUS_REVOKED
                || licenceStatus === LICENCE_STATUS_SUSPENDED
                || licenceStatus === LICENCE_STATUS_WITHDRAWN
                || licenceStatus === LICENCE_STATUS_WITHDRAWN_BY_CLIENT)
                dispatch(setCurrentAgentLicenceExpiryDate(new Date().getTime()))
        }

        setSubmitRequestStatus(REQUEST_STATUS.IN_PROGRESS)

        dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
            .then(data => {
                // send the email to the business contact
                if(!isAgencyApplication) {
                    if(primaryAgencyApplication) { 
                        sendMessageToAgency(primaryAgencyApplication.businessLicenceData?.mainBusinessContact?.emailAddress)
                    } else { 
                        dispatch(fetchApplicationWithId(licenceApplication.businessApplicationId))
                            .then(mainAgencyApplication => {
                                if(mainAgencyApplication?.businessLicenceData?.mainBusinessContact?.emailAddress) {
                                    sendMessageToAgency(mainAgencyApplication?.businessLicenceData?.mainBusinessContact?.emailAddress)
                                }
                            })
                            .catch(error => {
                                setRequestError("error in sending message to agency.")
                            })
                    }
                    
                } else { 
                    if(licenceApplication?.businessLicenceData?.mainBusinessContact?.emailAddress) {
                        sendMessageToAgency(licenceApplication?.businessLicenceData?.mainBusinessContact?.emailAddress)
                    }
                }
            })
            .catch(error => {
                setRequestError("error in changing licence status.")
            })
            .finally(() => {
                setSubmitRequestStatus(REQUEST_STATUS.IDLE)
            })
    }

    return (
        <>
            <div className="decisionFormItem">
                <div className="decisionFormItemInfo bottomMarginNormal">
                    Use this form to change a licence status and inform the licensee of the change.
                </div>
                <div className="bottomMarginMedium">
                    <h2>Change licence status</h2>
                </div>

                <div style={{display: "flex"}} className="bottomMarginMedium">
                    <div style={{width: "250px", marginLeft : "10px"}}>
                        <span style={{fontSize :"1em", fontWeight: "bold"}}>
                            Licence status
                        </span>
                    </div>
                    <div className="edit-licence-status-form">
                        <fieldset className="ns-fieldset">
                            <div className={"ns-radios ns-radios--small"}>
                                <div key={"active-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"active-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_ACTIVE} 
                                        checked={licenceStatus === LICENCE_STATUS_ACTIVE}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-active-input"} 
                                        name={"edit-licence-status-form-active-input"}
                                    />
                                    <label 
                                        key={"active-status-label"}
                                        id={"edit-licence-status-form-active-label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-active-input"}>
                                            <div className="licence-status-approved">
                                                <i className="fa fa-check-circle fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Active</span>
                                    </label>
                                </div>
                                <div key={"pending-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"pending-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_SUSPENDED} 
                                        checked={licenceStatus === LICENCE_STATUS_SUSPENDED}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-pending-input"} 
                                        name={"edit-licence-status-form-pending-input"}
                                    />
                                    <label 
                                        key={"pending-status-label"}
                                        id={"edit-licence-status-form-pending--label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-pending-input"}>
                                            <div className="licence-status-pending">
                                                <i className="fa fa-clock-o fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Suspend</span>
                                    </label>
                                </div>
                                <div key={"expired-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"expired-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_EXPIRED} 
                                        checked={licenceStatus === LICENCE_STATUS_EXPIRED}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-expired-input"} 
                                        name={"edit-licence-status-form-expired-input"} 
                                    />
                                    <label 
                                        key={"expired-status-label"}
                                        id={"edit-licence-status-form-expired--label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-expired-input"}>
                                            <div className="licence-status-expired">
                                                <i className="fa fa-exclamation-circle fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Expired</span>
                                    </label>
                                </div>
                                <div key={"revoked-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"revoked-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_REVOKED} 
                                        checked={licenceStatus === LICENCE_STATUS_REVOKED}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-revoked-input"} 
                                        name={"edit-licence-status-form-revoked-input"} 
                                    />
                                    <label 
                                        key={"revoked-status-label"}
                                        id={"edit-licence-status-form-revoked--label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-revoked-input"}>
                                            <div className="licence-status-withdrawn">
                                                <i className="fa fa-exclamation-circle fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Revoke</span>
                                    </label>
                                </div>
                                <div key={"withdrawn-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"withdrawn-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_WITHDRAWN} 
                                        checked={licenceStatus === LICENCE_STATUS_WITHDRAWN}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-withdrawn-input"} 
                                        name={"edit-licence-status-form-withdrawn-input"} 
                                    />
                                    <label 
                                        key={"withdrawn-status-label"}
                                        id={"edit-licence-status-form-withdrawn--label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-withdrawn-input"}>
                                            <div className="licence-status-withdrawn">
                                                <i className="fa fa-exclamation-circle fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Cancelled</span>
                                    </label>
                                </div>
                                <div key={"withdrawn-by-client-status"} className="ns-radios__item">
                                    <input 
                                        className="ns-radios__input"
                                        key={"withdrawn-by-client-status-input"}
                                        type={"radio"} 
                                        value={LICENCE_STATUS_WITHDRAWN_BY_CLIENT} 
                                        checked={licenceStatus === LICENCE_STATUS_WITHDRAWN_BY_CLIENT}
                                        onChange={handleLicenceStatusChange}
                                        id={"edit-licence-status-form-withdrawn-by-client-input"} 
                                        name={"edit-licence-status-form-withdrawn-by-client-input"} 
                                    />
                                    <label 
                                        key={"withdrawn-by-client-status-label"}
                                        id={"edit-licence-status-form-withdrawn-by-client--label"}
                                        className="ns-radios__label"
                                        htmlFor={"edit-licence-status-form-withdrawn-by-client-input"}>
                                            <div className="licence-status-withdrawn">
                                                <i className="fa fa-exclamation-circle fa-lg"></i>
                                            </div>
                                            <span className="ns-label-span bltReviewDecisionLabel">Cancelled by client</span>
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>

                <LineSeperator />

                <div className="bottomMarginMedium">
                    <h2>Message to agency</h2>
                </div>

                <div className="bottomMarginMedium" style={{display: "flex"}}>
                    <div style={{width:"250px", marginLeft : "10px"}}>
                        <div style={{fontSize : "1em", fontWeight: "bold"}}>
                            {businessContactName} ,
                        </div>
                        <div style={{fontSize : "1em", fontWeight: "bold"}}>
                            Business contact
                        </div>
                    </div>
                    <div className="edit-licence-status-form">
                        <fieldset className="ns-fieldset">
                            <div className={"textarea fw-25 inlineDisplay"}>
                                <textarea
                                    className={"ns-input"}
                                    style={{width: "25em"}}
                                    id={"edit-licence-status-agency-message-input"}
                                    rows={4} 
                                    cols={60}
                                    maxLength={1000}
                                    value={emailMessage || ""}
                                    onInput={(e) => {
                                        setEmailMessage(e.target.value)
                                    }}>
                                </textarea>
                            </div>
                        </fieldset>
                    </div>
                </div>
                                    
                <LineSeperator />

                <div className="bottomMarginMedium" style={{display: "flex"}}>
                    <div style={{width: "250px", marginLeft : "10px"}}>
                        <span style={{fontSize :"1.2em", fontWeight: "600"}}>
                            Submit
                        </span>
                    </div>
                    <div className={"edit-status-action-buttons" + requestError ? " has-error" : ""}>
                        <Button
                            style={{marginRight: "20px"}} 
                            text="Submit"
                            disabled={licenceStatus === licenceApplication.licenceStatus || submitRequestStatus === REQUEST_STATUS.IN_PROGRESS}
                            showSpinner={submitRequestStatus === REQUEST_STATUS.IN_PROGRESS}
                            buttonType="regular_success"
                            onClick={handleSubmit}
                        />
                        {requestError && ErrorMark()}
                        {requestError && <div className="help-block">
                            {requestError}
                        </div>}
                    </div>
                </div>

            </div>
        </>
    )
}
